// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/main/ai/ai-fast-path.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * Plain commands that need no model: "play", "pausa", "stop met draaien"…
 * Matched on the whole message, in the four chat languages, so a model
 * outage or an exhausted allowance never stops the sphere from answering
 * them, and they cost nothing (docs/AI_CHATBOT_PLAN.md §4).
 */
import type { AiChatLanguage } from '../../shared/ai-languages';
import type { AgentCommand } from '../../shared/protocol';

export interface FastPathMatch {
  command: AgentCommand;
  /** The reply once the command succeeds, in the session language. */
  reply: Record<AiChatLanguage, string>;
}

interface FastPathRule {
  pattern: RegExp;
  command: AgentCommand;
  reply: Record<AiChatLanguage, string>;
}

// Checked in order: "stop rotating" must win over "stop".
const RULES: ReadonlyArray<FastPathRule> = [
  {
    pattern: /^(stop (rotating|rotation|spinning)|deja de girar|para de girar|det[eé]n la rotaci[oó]n|stop met draaien|niet (meer )?draaien|deixa de girar|para de girar|atura la rotaci[oó])$/,
    command: { type: 'view.rotation', enabled: false },
    reply: { en: 'Rotation stopped.', es: 'Rotación detenida.', nl: 'Rotatie gestopt.', ca: 'Rotació aturada.' }
  },
  {
    pattern: /^(rotate|spin|start rotating|gira|girar|rota|rotar|draai|draaien|roteer|gira|girar)$/,
    command: { type: 'view.rotation', enabled: true },
    reply: { en: 'Rotating.', es: 'Girando.', nl: 'Draait.', ca: 'Girant.' }
  },
  {
    pattern: /^(play|resume|start|reproduce|reproducir|contin[uú]a|reanuda|speel af|afspelen|hervat|reprodueix|reproduir|comen[cç]a)$/,
    command: { type: 'media.play' },
    reply: { en: 'Playing.', es: 'Reproduciendo.', nl: 'Afspelen.', ca: 'Reproduint.' }
  },
  {
    pattern: /^(pause|pausa|pausar|pauze|pauzeer|pauzeren)$/,
    command: { type: 'media.pause' },
    reply: { en: 'Paused.', es: 'En pausa.', nl: 'Gepauzeerd.', ca: 'En pausa.' }
  },
  {
    pattern: /^(stop|para|parar|det[eé]n|detener|stoppen|atura|aturar)$/,
    command: { type: 'media.stop' },
    reply: { en: 'Stopped.', es: 'Detenido.', nl: 'Gestopt.', ca: 'Aturat.' }
  }
];

/** Lower case, no surrounding punctuation or courtesy words. */
export function normalizeCommandText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[¡!¿?.,;:]+/g, ' ')
    .replace(/\b(please|por favor|alsjeblieft|alstublieft|si us plau|siusplau)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function matchFastPath(text: string): FastPathMatch | null {
  const normalized = normalizeCommandText(text);
  for (const rule of RULES) {
    if (rule.pattern.test(normalized)) return { command: rule.command, reply: rule.reply };
  }
  return null;
}

/**
 * "load / show / open <title>" in the four languages: the requested title, to
 * be matched exactly against the catalog before any model is asked.
 */
export function matchLoadRequest(text: string): string | null {
  const normalized = normalizeCommandText(text);
  const match = /^(load|show|open|carga|cargar|muestra|mostrar|abre|pon|laad|toon|open|carrega|mostra|obre|posa)\s+(.+)$/.exec(normalized);
  const title = match?.[2];
  return title ? title.replace(/^(the|el|la|los|las|de|het|els|les)\s+/, '').trim() : null;
}

// "take me to the year 1960", "llévame al año 1960", "ga naar 1960",
// "porta'm a l'any 1960", or just "1960": a verb, a preposition, an article
// and "year", each optional, then the year and nothing else.
const YEAR_REQUEST = new RegExp(
  '^(?:(?:go|take me|jump|skip|move|show me|show|ve|ir|vamos|llévame|llevame|lleva|salta|muéstrame|muestra|enséñame|pon|ga|breng me|spring|toon|vés|ves|anem|porta\'m|porta’m|porta|mostra\'m|mostra’m|mostra|posa)\\s+)?' +
  '(?:(?:to|forward to|back to|a|al|hasta|naar|fins a|fins al)\\s+)?' +
  '(?:(?:the|el|het)\\s+)?' +
  '(?:(?:year|año|jaar|any|l\'any|l’any)\\s+)?' +
  '(\\d{4})$'
);

/** A request to go to a year, in the four languages: the year, or null. */
export function matchYearRequest(text: string): number | null {
  const match = YEAR_REQUEST.exec(normalizeCommandText(text));
  const year = match ? Number(match[1]) : NaN;
  return year >= 1000 && year <= 2199 ? year : null;
}

export const SEEK_YEAR_REPLY: Record<AiChatLanguage, (year: number) => string> = {
  en: (year) => `Going to ${year}.`,
  es: (year) => `Vamos a ${year}.`,
  nl: (year) => `Naar ${year}.`,
  ca: (year) => `Anem a ${year}.`
};

export const YEAR_OUT_OF_RANGE_REPLY: Record<AiChatLanguage, (years: string) => string> = {
  en: (years) => `This dataset covers ${years}.`,
  es: (years) => `Este conjunto de datos abarca ${years}.`,
  nl: (years) => `Deze dataset beslaat ${years}.`,
  ca: (years) => `Aquest conjunt de dades abasta ${years}.`
};

export const LOADING_REPLY: Record<AiChatLanguage, (title: string) => string> = {
  en: (title) => `Showing ${title}.`,
  es: (title) => `Mostrando ${title}.`,
  nl: (title) => `${title} wordt getoond.`,
  ca: (title) => `Mostrant ${title}.`
};

/** When the model gave no words at all: after actions that worked, and otherwise. */
export const DONE_REPLY: Record<AiChatLanguage, string> = { en: 'Done.', es: 'Hecho.', nl: 'Klaar.', ca: 'Fet.' };
export const NO_ANSWER_REPLY: Record<AiChatLanguage, string> = {
  en: "Sorry, I couldn't find an answer to that. Could you ask it another way?",
  es: 'Lo siento, no he encontrado una respuesta. ¿Puedes preguntarlo de otra forma?',
  nl: 'Sorry, daar heb ik geen antwoord op gevonden. Kun je het anders vragen?',
  ca: "Ho sento, no he trobat cap resposta. Ho pots preguntar d'una altra manera?"
};

export const ACTION_FAILED_REPLY: Record<AiChatLanguage, (reason: string) => string> = {
  en: (reason) => `I couldn't do that: ${reason}`,
  es: (reason) => `No he podido hacerlo: ${reason}`,
  nl: (reason) => `Dat lukte niet: ${reason}`,
  ca: (reason) => `No ho he pogut fer: ${reason}`
};
